import z from "zod";

/*
 ** Checkout status and progress
 */

const stepStatusSchema = z.object({
  id: z.string().uuid(),
  is_complete: z.boolean(),
});

export const checkoutProgressSchema = z.object({
  id: z.number(),
  checkout_snapshot_id: z.number(),
  status: z.enum(["incomplete", "complete"]),
  step_statuses: z.array(stepStatusSchema),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

/*
 ** Data Step
 */

export const checkoutApplicationFieldTypeSchema = z.enum([
  "addresses",
  "currency",
  "date",
  "float",
  "ssn_itin",
  "integer",
  "options",
  "sub_options",
  "percentage",
  "string",
  "url",
  "text",
  "boolean",
  "email",
  "pii",
  "phone_numbers",
  "phone_number_with_additional_phone_numbers",
  "personal_phone_numbers",
]);

const checkoutDataFieldSchema = z.object({
  id: z.string(),
  name: z.string(),
  label: z.string(),
  type: z.enum(["application_field", "custom_field"]).optional(),
  value_type: checkoutApplicationFieldTypeSchema.optional(),
  block: z.string().optional(),
});

const checkoutDataTableSchema = z.object({
  id: z.string(),
  type: z.enum(["table"]),
  layout: z.enum(["table", "form"]),
  fields: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      label: z.string(),
    })
  ),
  block: z.string(),
  title: z.string().optional(),
});

const ssnItinSchema = z.object({
  ssn: z.string().nullable().optional(),
  itin: z.string().nullable().optional(),
});

const addressSchema = z.object({
  id: z.number().optional(),
  address_line: z.string(),
  address_line2: z.string().nullable().optional(),
  country: z.string(),
  state: z.string(),
  city: z.string(),
  zip: z.string(),
  is_primary: z.boolean(),
});

// This `any` can likely be fixed up when we move to a monorepo, and gain
// access to all the types for application blocks.
const checkoutDataValue = z.any().optional();

const checkoutDataApplicationBlock = z.record(z.string(), checkoutDataValue);
const checkoutDataApplicationTableBlock = z.array(z.record(z.string(), checkoutDataValue));

const checkoutDataBlock = z.union([
  checkoutDataApplicationBlock,
  checkoutDataApplicationTableBlock,
]);
const checkoutDataValues = z.record(z.string(), checkoutDataBlock).nullable();

const checkoutDataStepItemSchema = z.discriminatedUnion("type", [
  checkoutDataFieldSchema,
  checkoutDataTableSchema,
]);

/*
 ** Step type literals
 */

const checkoutStepTypeDocumentsSchema = z.literal("documents");
const checkoutStepTypeConsentsSchema = z.literal("consents");
const checkoutStepTypeContractsSchema = z.literal("contracts");
const checkoutStepTypeDataSchema = z.literal("data");
const checkoutStepTypeServiceSchema = z.literal("service");
const checkoutStepTypeCustomSchema = z.literal("custom");

export const checkoutStepTypeSchema = z.union([
  checkoutStepTypeDocumentsSchema,
  checkoutStepTypeConsentsSchema,
  checkoutStepTypeContractsSchema,
  checkoutStepTypeDataSchema,
  checkoutStepTypeServiceSchema,
  checkoutStepTypeCustomSchema,
]);

export const serviceIdSchema = z.enum(["chart", "vouched_verification"]);

/*
 ** Step schemas
 */

const checkoutStepBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
});

const checkoutDocumentsStepSchema = checkoutStepBaseSchema.extend({
  type: checkoutStepTypeDocumentsSchema,
});

const checkoutConsentsStepSchema = checkoutStepBaseSchema.extend({
  type: checkoutStepTypeConsentsSchema,
  templates: z.array(
    z.object({
      id: z.string(),
      name: z.string().optional(),
    })
  ),
});

const checkoutContractsStepSchema = checkoutStepBaseSchema.extend({
  type: checkoutStepTypeContractsSchema,
  templates: z.array(
    z.object({
      id: z.string(),
      name: z.string().optional(),
    })
  ),
});

const checkoutDataStepSchema = checkoutStepBaseSchema.extend({
  type: checkoutStepTypeDataSchema,
  items: z.array(checkoutDataStepItemSchema),
});

const checkoutServiceStepSchema = checkoutStepBaseSchema.extend({
  type: checkoutStepTypeServiceSchema,
  service_id: serviceIdSchema,
});

const checkoutCustomStepSchema = checkoutStepBaseSchema.extend({
  type: checkoutStepTypeCustomSchema,
  custom_title: z.string().nullish(),
  custom_description: z.string().nullish(),
  custom_button_text: z.string().nullish(),
  custom_button_url: z.string().nullish(),
});

export const checkoutStepSchema = z.discriminatedUnion("type", [
  checkoutDocumentsStepSchema,
  checkoutConsentsStepSchema,
  checkoutContractsStepSchema,
  checkoutDataStepSchema,
  checkoutServiceStepSchema,
  checkoutCustomStepSchema,
]);

/*
 ** Custom step URL visit schema
 */

export const customStepUrlVisitSchema = z.object({
  url: z.string(),
  visited_at: z.string(),
});

/*
 ** Template basics
 */

export const checkoutFinalizeContentSchema = z.object({
  title: z.string().nullable(),
  description: z.string().nullable(),
});

export const checkoutTemplateBaseSchema = z.object({
  id: z.number(),
});

export const checkoutTemplateSchema = checkoutTemplateBaseSchema.extend({
  content: z.object({
    id: z.string().optional(),
    version: z.string().optional(),
    title: z.string(),
    subtitle: z.string().nullable(),
    checkout_section_name: z.string().nullable().optional(),
    offer_summary_section_name: z.string().nullable().optional(),
    steps: z.array(checkoutStepSchema),
    finalize: checkoutFinalizeContentSchema,
  }),
  data_step_values: checkoutDataValues,
});

/*
 ** Type exports
 */

export type Address = z.infer<typeof addressSchema>;
export type SsnItin = z.infer<typeof ssnItinSchema>;

export type CheckoutProgress = z.infer<typeof checkoutProgressSchema>;

export type CheckoutDataFieldType = z.infer<typeof checkoutApplicationFieldTypeSchema>;
export type CheckoutDataValue = z.infer<typeof checkoutDataValue>;
export type CheckoutDataValues = z.infer<typeof checkoutDataValues>;
export type CheckoutDataField = z.infer<typeof checkoutDataFieldSchema>;
export type CheckoutDataTable = z.infer<typeof checkoutDataTableSchema>;
export type CheckoutDataBlock = z.infer<typeof checkoutDataBlock>;
export type CheckoutDataApplicationBlock = z.infer<typeof checkoutDataApplicationBlock>;
export type CheckoutDataApplicationTableBlock = z.infer<typeof checkoutDataApplicationTableBlock>;
export type CheckoutDataItem = z.infer<typeof checkoutDataStepItemSchema>;

export type CheckoutStep = z.infer<typeof checkoutStepSchema>;
export type CheckoutStepType = z.infer<typeof checkoutStepTypeSchema>;
export type CheckoutDocumentsStep = z.infer<typeof checkoutDocumentsStepSchema>;
export type CheckoutConsentsStep = z.infer<typeof checkoutConsentsStepSchema>;
export type CheckoutContractsStep = z.infer<typeof checkoutContractsStepSchema>;
export type CheckoutDataStep = z.infer<typeof checkoutDataStepSchema>;
export type CheckoutServiceStep = z.infer<typeof checkoutServiceStepSchema>;
export type CheckoutCustomStep = z.infer<typeof checkoutCustomStepSchema>;
export type ServiceId = z.infer<typeof serviceIdSchema>;
export type CustomStepUrlVisit = z.infer<typeof customStepUrlVisitSchema>;

export type CheckoutTemplateBase = z.infer<typeof checkoutTemplateBaseSchema>;
export type CheckoutTemplate = z.infer<typeof checkoutTemplateSchema>;
